import {
  createMetastaticProstateClinicCustomResolver,
  createMetastaticProstateClinicDefaultFormValues,
} from '@ldt/features/orders/metastatic_prostate_clinic_form';
import {
  createMetastaticProstateTempusCustomResolver,
  createMetastaticProstateTempusDefaultFormValues,
} from '@ldt/features/orders/metastatic_prostate_tempus_form';
import {
  createProstateBiopsyCustomResolver,
  createProstateBiopsyDefaultFormValues,
} from '@ldt/features/orders/prostate_biopsy_form';
import {
  createProstatePostRPCustomResolver,
  createProstatePostRPDefaultFormValues,
} from '@ldt/features/orders/prostate_post_rp_form';
import { canUseClinicalNotProvided } from '@ldt/features/orders/utils/clinical_not_provided';
import { AuthenticationService } from 'src/api/authentication_service';
import {
  OrderingPathway,
  LdtOrderDetailFieldsFragment,
  SpecimenType,
} from 'src/types';
import { WorkflowType } from 'src/types/workflows';

export const hasLdtValidationErrors = async (
  order: LdtOrderDetailFieldsFragment,
  isCSOrSalesUser: boolean,
  workflowType: WorkflowType,
  isEditingQCPendingOrder: boolean,
  specimenType: SpecimenType,
): Promise<boolean> => {
  const allowTStageNotProvided = canUseClinicalNotProvided(
    isCSOrSalesUser,
    AuthenticationService.getInstance().getEffectiveOrganization().features
      .allowTStageNotProvided,
    workflowType,
  );

  if (specimenType === SpecimenType.ProstateBiopsy) {
    const formData = createProstateBiopsyDefaultFormValues(order);
    const resolver = createProstateBiopsyCustomResolver(
      isCSOrSalesUser,
      workflowType,
      order.orderingPathway,
      isEditingQCPendingOrder,
      undefined,
      allowTStageNotProvided,
    );
    const result = await resolver(
      formData,
      {},
      {
        fields: {},
        shouldUseNativeValidation: false,
      },
    );

    return Object.keys(result.errors).length > 0;
  } else if (specimenType === SpecimenType.ProstatePostRp) {
    const formData = createProstatePostRPDefaultFormValues(order);
    const resolver = createProstatePostRPCustomResolver(
      isCSOrSalesUser,
      order.orderingPathway,
      isEditingQCPendingOrder,
      undefined,
      allowTStageNotProvided,
      workflowType,
    );

    const result = await resolver(
      formData,
      {},
      {
        fields: {},
        shouldUseNativeValidation: false,
      },
    );

    return Object.keys(result.errors).length > 0;
  } else if (specimenType === SpecimenType.MetastaticProstateBiopsy) {
    if (
      [
        WorkflowType.CLINIC,
        WorkflowType.DIRECT_UPLOAD_LAB,
        WorkflowType.ANCILLARY_LAB,
      ].includes(workflowType)
    ) {
      const formData = createMetastaticProstateClinicDefaultFormValues(order);
      const resolver = createMetastaticProstateClinicCustomResolver(
        isCSOrSalesUser,
        order.orderingPathway ?? OrderingPathway.DefaultNonReflexive,
        isEditingQCPendingOrder,
        undefined,
        allowTStageNotProvided,
        workflowType,
      );
      const result = await resolver(
        formData,
        {},
        { fields: {}, shouldUseNativeValidation: false },
      );

      return Object.keys(result.errors).length > 0;
    } else if (workflowType === WorkflowType.TEMPUS) {
      const formData = createMetastaticProstateTempusDefaultFormValues(order);
      const resolver = createMetastaticProstateTempusCustomResolver(false);
      const result = await resolver(
        formData,
        {},
        { fields: {}, shouldUseNativeValidation: false },
      );

      return Object.keys(result.errors).length > 0;
    }

    throw new Error(`Unsupported workflow type: ${workflowType}`);
  } else {
    throw new Error(`Unsupported specimen type: ${specimenType}`);
  }
};
